<template>
  <div class="admin-reports">
    <div
      v-if="!isAdmin"
      class="text-center text-muted py-3"
    >
      No tienes permisos para ver este reporte.
    </div>

    <template v-else>
      <b-row class="align-items-center mb-2">
        <b-col>
          <h1 class="font-weight-bolder mb-0">
            Clientes Mensual
          </h1>
          <small class="text-muted">
            {{ tableTitle }}
          </small>
        </b-col>

        <b-col
          cols="12"
          lg="6"
          class="mt-1 mt-lg-0"
        >
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="fromDate"
              type="date"
              class="mr-1"
            />

            <b-form-input
              v-model="toDate"
              type="date"
              class="mr-1"
            />

            <b-form-select
              v-model.number="selectedYear"
              :options="yearOptions"
              class="mr-1 year-select"
            />

            <b-button
              variant="primary"
              :disabled="loading"
              @click="loadReport"
            >
              Cargar
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-overlay
        :show="loading"
        opacity="0.12"
      >
        <b-alert
          v-if="errorMessage"
          show
          variant="danger"
          class="mb-2"
        >
          {{ errorMessage }}
        </b-alert>

        <b-card
          v-if="visibleMonthNumbers.length"
          no-body
        >
          <div class="table-responsive">
            <table class="table table-sm mb-0 report-table">
              <thead>
                <tr class="month-row">
                  <th class="text-left customer-col">
                    Cliente
                  </th>
                  <th class="text-left email-col">
                    Correo
                  </th>
                  <th class="text-left phone-col">
                    Teléfono
                  </th>

                  <th
                    v-for="monthNumber in visibleMonthNumbers"
                    :key="`month-${monthNumber}`"
                    class="text-center month-col"
                  >
                    {{ monthHeader(monthNumber) }}
                  </th>

                  <th class="text-center total-col">
                    Total
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(customer, rowIndex) in customers"
                  :key="rowKey(customer, rowIndex)"
                >
                  <td class="text-left customer-col">
                    {{ customer.customer_name || '-' }}
                    <b-badge
                      v-if="customer.quoted_only"
                      variant="warning"
                      class="ml-1"
                    >
                      Solo cotizó
                    </b-badge>
                  </td>
                  <td class="text-left email-col">
                    {{ customer.customer_email || '-' }}
                  </td>
                  <td class="text-left phone-col">
                    {{ customer.customer_phone || '-' }}
                  </td>

                  <td
                    v-for="monthNumber in visibleMonthNumbers"
                    :key="`${rowKey(customer, rowIndex)}-month-${monthNumber}`"
                    class="text-center"
                    :class="cellClass(customer, monthNumber)"
                  >
                    {{ displayValue(customer, monthNumber) }}
                  </td>

                  <td class="text-center font-weight-bold total-col">
                    {{ rowTotal(customer) }}
                  </td>
                </tr>

                <tr v-if="!customers.length">
                  <td
                    class="text-center text-muted py-2"
                    :colspan="visibleMonthNumbers.length + 4"
                  >
                    No hay clientes con actividad para el periodo seleccionado.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>

        <b-alert
          v-else-if="!loading"
          show
          variant="light"
        >
          No hay meses para el rango seleccionado.
        </b-alert>
      </b-overlay>
    </template>
  </div>
</template>

<script>
import axios from '@axios'

function defaultMonthLabels() {
  return [
    'ENERO',
    'FEBRERO',
    'MARZO',
    'ABRIL',
    'MAYO',
    'JUNIO',
    'JULIO',
    'AGOSTO',
    'SEPTIEMBRE',
    'OCTUBRE',
    'NOVIEMBRE',
    'DICIEMBRE',
  ]
}

function dateInputValue(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function defaultDateRange() {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  return {
    fromDate: `${currentYear}-01-01`,
    toDate: dateInputValue(currentDate),
    selectedYear: currentYear,
  }
}

export default {
  name: 'AdminReportsCustomerMonthly',
  data() {
    const defaults = defaultDateRange()
    return {
      loading: false,
      fromDate: defaults.fromDate,
      toDate: defaults.toDate,
      selectedYear: defaults.selectedYear,
      errorMessage: '',
      reportData: null,
    }
  },
  computed: {
    reportPayload() {
      const raw = this.reportData
      if (!raw || typeof raw !== 'object') return {}

      const hasTopLevelFields = ['months', 'matrix', 'years', 'rows']
        .some(key => Object.prototype.hasOwnProperty.call(raw, key))

      if (hasTopLevelFields) return raw
      if (raw.data && typeof raw.data === 'object') return raw.data
      return raw
    },
    isAdmin() {
      const user = this.$store.getters['auth/currentUser'] || {}
      return user.role_name === 'admin'
    },
    monthLabels() {
      const months = this.reportPayload?.months
      if (Array.isArray(months) && months.length) return months
      return defaultMonthLabels()
    },
    monthNumbers() {
      return this.monthLabels.map((_, idx) => idx + 1)
    },
    availableYears() {
      const payloadYears = Array.isArray(this.reportPayload?.years) ? this.reportPayload.years : []
      const matrixYears = Array.isArray(this.reportPayload?.matrix)
        ? this.reportPayload.matrix.map(item => Number(item?.year)).filter(Boolean)
        : []

      const years = [...payloadYears, ...matrixYears]
        .map(item => Number(item))
        .filter(year => Number.isFinite(year))

      if (!years.length) return [Number(this.selectedYear || new Date().getFullYear())]
      return [...new Set(years)].sort((a, b) => a - b)
    },
    yearOptions() {
      return this.availableYears.map(year => ({
        value: year,
        text: year,
      }))
    },
    matrixByYear() {
      const matrix = Array.isArray(this.reportPayload?.matrix) ? this.reportPayload.matrix : []
      return matrix.reduce((acc, item) => {
        const year = Number(item?.year)
        if (!year) return acc

        let rows = item?.rows ?? item?.matrix ?? []
        if (typeof rows === 'string') {
          try {
            const parsed = JSON.parse(rows)
            rows = Array.isArray(parsed) ? parsed : []
          } catch (error) {
            rows = []
          }
        }
        if (!Array.isArray(rows) && rows && typeof rows === 'object') {
          rows = Object.values(rows)
        }

        acc[year] = Array.isArray(rows) ? rows : []
        return acc
      }, {})
    },
    selectedRowsRaw() {
      return this.matrixByYear[Number(this.selectedYear)] || []
    },
    customers() {
      const rows = this.selectedRowsRaw
        .map(row => this.normalizeCustomer(row))
        .filter(Boolean)

      return rows
        .slice()
        .sort((a, b) => {
          const aLead = a?.quoted_only ? 1 : 0
          const bLead = b?.quoted_only ? 1 : 0
          if (aLead !== bLead) return aLead - bLead
          if (aLead === 1) return Number(b?.total_quoted || 0) - Number(a?.total_quoted || 0)
          return Number(b?.total_general || 0) - Number(a?.total_general || 0)
        })
    },
    visibleMonthNumbers() {
      const from = this.fromDate ? new Date(`${this.fromDate}T00:00:00`) : null
      const to = this.toDate ? new Date(`${this.toDate}T23:59:59`) : null
      if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return this.monthNumbers

      return this.monthNumbers.filter(monthNumber => {
        const startOfMonth = new Date(Number(this.selectedYear), monthNumber - 1, 1)
        const endOfMonth = new Date(Number(this.selectedYear), monthNumber, 0, 23, 59, 59, 999)
        return endOfMonth >= from && startOfMonth <= to
      })
    },
    monthTotals() {
      return this.visibleMonthNumbers.reduce((acc, monthNumber) => {
        const total = this.customers.reduce((sum, customer) => sum + this.valueFor(customer, monthNumber), 0)
        acc[monthNumber] = total
        return acc
      }, {})
    },
    periodLabel() {
      if (!this.visibleMonthNumbers.length) return 'Sin meses'
      const first = this.monthName(this.visibleMonthNumbers[0])
      const last = this.monthName(this.visibleMonthNumbers[this.visibleMonthNumbers.length - 1])
      if (first === last) return first
      return `${first} y ${last}`
    },
    tableTitle() {
      return `TABLA: Compradas por cliente - ${this.periodLabel} (meses) - ${this.selectedYear}`
    },
  },
  watch: {
    customers() {
      this.updateStickyOffsets()
    },
  },
  created() {
    if (this.isAdmin) {
      this.loadReport()
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateStickyOffsets)
    this.updateStickyOffsets()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateStickyOffsets)
  },
  methods: {
    updateStickyOffsets() {
      this.$nextTick(() => {
        const table = this.$el && this.$el.querySelector('.report-table')
        if (!table) return

        const headCells = table.querySelectorAll('thead tr th')
        if (headCells.length < 3) return

        const customerWidth = headCells[0].getBoundingClientRect().width
        const emailWidth = headCells[1].getBoundingClientRect().width

        table.style.setProperty('--sticky-left-2', `${customerWidth}px`)
        table.style.setProperty('--sticky-left-3', `${customerWidth + emailWidth}px`)
      })
    },
    syncSelectedYear() {
      const years = this.availableYears
      if (years.includes(Number(this.selectedYear))) return

      const fromYear = Number((this.fromDate || '').slice(0, 4))
      if (years.includes(fromYear)) {
        this.selectedYear = fromYear
        return
      }

      this.selectedYear = years[years.length - 1]
    },
    rowKey(customer, index) {
      const key = customer?.customer_id || customer?.customer_email || customer?.customer_name || 'customer'
      return `${key}-${index}`
    },
    normalizeCustomer(row) {
      if (!row || typeof row !== 'object') return null

      const nestedCustomer = (row.customer && typeof row.customer === 'object') ? row.customer : {}
      const rowMonths = Array.isArray(row.months) ? row.months : []
      const objectMonths = (!rowMonths.length && row.months && typeof row.months === 'object')
        ? Object.keys(row.months).map(monthKey => ({
          month: Number(monthKey),
          purchased: Number(row.months[monthKey] || 0),
        }))
        : []

      const monthsSource = rowMonths.length ? rowMonths : objectMonths
      const normalizedMonths = monthsSource.map(item => ({
        month: Number(item?.month || item?.index || 0),
        purchased: Number(item?.purchased || item?.value || item?.total || 0),
        quoted: Number(item?.quoted || 0),
      }))

      const totalByMonths = normalizedMonths.reduce((sum, item) => sum + Number(item.purchased || 0), 0)
      const quotedByMonths = normalizedMonths.reduce((sum, item) => sum + Number(item.quoted || 0), 0)

      return {
        customer_id: row.customer_id || row.id || nestedCustomer.id || '',
        customer_name: row.customer_name || row.name || nestedCustomer.name || '-',
        customer_email: row.customer_email || row.email || nestedCustomer.email || '-',
        customer_phone: row.customer_phone || row.phone || nestedCustomer.phone || '-',
        months: normalizedMonths,
        total_general: Number(row.total_general || row.total || row.total_purchased || totalByMonths),
        total_quoted: Number(row.total_quoted || quotedByMonths),
        quoted_only: row.quoted_only === true || row.quoted_only === 'true',
      }
    },
    monthName(monthNumber) {
      return this.monthLabels[Number(monthNumber) - 1] || `MES ${monthNumber}`
    },
    monthHeader(monthNumber) {
      const total = Number(this.monthTotals?.[monthNumber] || 0)
      return `${this.monthName(monthNumber)} (${total})`
    },
    valueFor(customer, monthNumber) {
      if (!customer || !Array.isArray(customer.months)) return 0
      const found = customer.months.find(item => Number(item.month) === Number(monthNumber))
      return Number(found?.purchased || 0)
    },
    quotedFor(customer, monthNumber) {
      if (!customer || !Array.isArray(customer.months)) return 0
      const found = customer.months.find(item => Number(item.month) === Number(monthNumber))
      return Number(found?.quoted || 0)
    },
    displayValue(customer, monthNumber) {
      if (customer?.quoted_only) return this.quotedFor(customer, monthNumber)
      return this.valueFor(customer, monthNumber)
    },
    rowTotal(customer) {
      if (customer?.quoted_only) return Number(customer?.total_quoted || 0)
      return Number(customer?.total_general || 0)
    },
    cellClass(customer, monthNumber) {
      if (customer?.quoted_only) {
        return this.quotedFor(customer, monthNumber) > 0 ? 'cell-quoted' : 'cell-zero'
      }

      const currentValue = Number(this.valueFor(customer, monthNumber) || 0)
      const previousValue = monthNumber > 1 ? Number(this.valueFor(customer, monthNumber - 1) || 0) : 0

      if (previousValue === 0 && currentValue > 0) return 'cell-green'
      if (previousValue > 0 && currentValue > 0) return 'cell-blue'
      if (previousValue > 0 && currentValue === 0) return 'cell-red'
      return 'cell-zero'
    },
    async loadReport() {
      if (!this.fromDate || !this.toDate) {
        this.errorMessage = 'Selecciona un rango de fechas válido.'
        return
      }

      const from = new Date(`${this.fromDate}T00:00:00`)
      const to = new Date(`${this.toDate}T23:59:59`)
      if (from > to) {
        this.errorMessage = 'La fecha inicial no puede ser mayor a la fecha final.'
        return
      }

      this.loading = true
      this.errorMessage = ''

      try {
        const params = {
          from: this.fromDate,
          to: this.toDate,
        }

        const { data } = await axios.get('/v1/reports/quotes_purchased_by_customer', {
          params,
        })

        this.reportData = data || {}
        this.syncSelectedYear()
      } catch (error) {
        const responseData = error?.response?.data || {}
        this.errorMessage = responseData.message || responseData.error || 'No se pudo cargar el reporte mensual por cliente'
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-reports {
  display: flex;
  flex-direction: column;
}

.year-select {
  min-width: 110px;
}

.table-responsive {
  max-height: calc(100vh - 220px);
  overflow: auto;
}

.report-table {
  color: #111;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    vertical-align: middle;
    border-right: 1px solid #bfbfbf;
    border-bottom: 1px solid #bfbfbf;
    white-space: nowrap;
    padding: 0.3rem 0.55rem;
    line-height: 1.2;
  }

  th:first-child,
  td:first-child {
    border-left: 1px solid #bfbfbf;
  }

  thead th {
    border-top: 1px solid #bfbfbf;
  }

  th {
    font-weight: 700;
    background-color: #fff;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .customer-col,
  .email-col,
  .phone-col {
    position: sticky;
    z-index: 1;
    background-color: #fff;
  }

  thead .customer-col,
  thead .email-col,
  thead .phone-col {
    z-index: 3;
  }
}

.month-row {
  th {
    background: #fff;
    text-transform: uppercase;
    font-size: 0.78rem;
    color: #6f6f78;
    letter-spacing: 0.02em;
  }
}

.customer-col {
  min-width: 180px;
  left: 0;
}

.email-col {
  min-width: 250px;
  left: var(--sticky-left-2, 180px);
}

.phone-col {
  min-width: 130px;
  left: var(--sticky-left-3, 430px);
}

.month-col {
  min-width: 140px;
}

.total-col {
  min-width: 100px;
}

.cell-zero {
  background-color: #d6d6d6;
}

.cell-green {
  background-color: #b7d7a8;
}

.cell-blue {
  background-color: #56c1cf;
}

.cell-red {
  background-color: #df9a9a;
}

.cell-quoted {
  background-color: #ffd966;
}
</style>
